import { css } from "@emotion/react";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { ExternalLink } from "@/components/ExternalLink";
import { MotifIcon } from "@/components/MotifIcon";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { defineStyles } from "@/internal/emotion";

const styles = defineStyles({
  icon: css({
    fontSize: "16px",
    color: theme.colors.staticFgNote,
  }),
});

interface ChannelStatusProps {
  status: InterludeTypes["ChannelWithInformation"]["status"];
  type: InterludeTypes["ChannelWithInformation"]["type"];
}

export const ChannelStatus: FC<ChannelStatusProps> = ({ status, type }) => {
  const { t } = useTranslation();

  switch (status) {
    case "connected":
      return <Badge variant="positive">{t("channels.status.connect")}</Badge>;
    case "disconnected":
      return <Badge variant="plain">{t("channels.status.disconnected")}</Badge>;
    case "token-expired":
      return <Badge variant="negative">{t("channels.status.expired")}</Badge>;

    case "warning":
      return type === "email" ? (
        <Stack direction="row" gap="4px" alignItems="center">
          <Badge variant="attentive">{t("channels.status.warning")}</Badge>
          <Tooltip
            placement="top"
            title={
              <Stack>
                <Typography variant="body">
                  {t("channels.status.warning.tooltip", {
                    defaultValue:
                      "Brand domain hard bounces > 2% (24h), or complaints > 0.1%; consider list cleanup. Need help? Contact us.",
                  })}
                </Typography>
                <ExternalLink
                  href={t("channels.status.warning.tooltip.linkUrl")}
                  trailingIcon={true}
                >
                  {t("channels.status.warning.tooltip.learnMore")}
                </ExternalLink>
              </Stack>
            }
            arrow={true}
          >
            <MotifIcon un-i-motif="circle_info" css={styles.icon} />
          </Tooltip>
        </Stack>
      ) : (
        <Badge variant="attentive">{t("channels.status.warning")}</Badge>
      );

    case "suspended":
      return (
        <Stack direction="row" gap="4px" alignItems="center">
          <Badge variant="negative">{t("channels.status.suspended")}</Badge>
          <Tooltip
            placement="top"
            title={
              <Stack>
                <Typography variant="body">
                  {t("channels.status.suspended.tooltip")}
                </Typography>
                <ExternalLink
                  href={t("channels.status.suspended.tooltip.linkUrl")}
                  trailingIcon={true}
                >
                  {t("channels.status.suspended.tooltip.learnMore")}
                </ExternalLink>
              </Stack>
            }
            arrow={true}
          >
            <MotifIcon un-i-motif="circle_info" css={styles.icon} />
          </Tooltip>
        </Stack>
      );
    case "not_set":
      return (
        <Stack direction="row" gap="4px" alignItems="center">
          <Badge variant="negative">{t("channels.status.not_set")}</Badge>
          <Tooltip
            placement="top"
            title={
              <Stack>
                <Typography variant="body">
                  {t("channels.status.not_set.tooltip")}
                </Typography>
                <ExternalLink
                  href={t("channels.status.not_set.tooltip.linkUrl")}
                  trailingIcon={true}
                >
                  {t("channels.status.not_set.tooltip.learnMore")}
                </ExternalLink>
              </Stack>
            }
            arrow={true}
          >
            <MotifIcon un-i-motif="circle_info" css={styles.icon} />
          </Tooltip>
        </Stack>
      );
    case "healthy":
      return <Badge variant="positive">{t("channels.status.healthy")}</Badge>;
    case "building_reputation":
      return (
        <Badge variant="positive">
          {t("channels.status.buildingReputation")}
        </Badge>
      );
    case "restricted":
      return (
        <Stack direction="row" gap="4px" alignItems="center">
          <Badge variant="attentive">{t("channels.status.restricted")}</Badge>
          <Tooltip
            placement="top"
            title={
              <Stack>
                <Typography variant="body">
                  {t("channels.status.restricted.tooltip")}
                </Typography>
                <ExternalLink
                  href={t("channels.status.restricted.tooltip.linkUrl")}
                  trailingIcon={true}
                >
                  {t("channels.status.restricted.tooltip.learnMore")}
                </ExternalLink>
              </Stack>
            }
            arrow={true}
          >
            <MotifIcon un-i-motif="circle_info" css={styles.icon} />
          </Tooltip>
        </Stack>
      );
    default:
      status satisfies never;
      return null;
  }
};
